import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes';
import { AnalyticEvents } from '@app/enums';
import { BadgeOptions } from '@app/interfaces';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { JobFunnelMessageTemplate } from '@app/models/recruiting/job-funnel-message-template.model';
import { JobFunnel } from '@app/models/recruiting/job-funnel.model';
import { JobPosting } from '@app/models/recruiting/job-posting.model';
import { ApplicantCopyActions } from '@app/modules/applicant-tracker/enums/applicant-copy-actions.enum';
import { JobPostingStatus } from '@app/modules/applicant-tracker/enums/job-posting-status.enum';
import { JobPostingApplicantIndex, PipelineShow } from '@app/modules/applicant-tracker/meta/view-contexts';
import {
    ApplicantIndividualMessageService,
    ApplicantProfileService,
    ApplicantService,
} from '@app/modules/applicant-tracker/services';
import { ApplicantProfileMenuService } from '@app/modules/applicant-tracker/services/menus/applicant-profile-menu.service';
import { HireEmployee } from '@app/modules/settings/meta/module-feature-permissions.meta';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { JobFunnelNameOptions } from '@app/types/translatables/applicant-tracker/job-funnel-name.options';
import { RoutingStateService } from '@common/services/routing-state.service';
import { format } from 'date-fns';
import { HireClickEvent, SendOfferClickEvent } from './components/hire-drop-down/hire-drop-down.component';

@Component({
    selector: 'app-applicant-show',
    templateUrl: './applicant-show.template.html',
    styleUrls: ['./applicant-show.style.scss'],
})
export class ApplicantShowView implements OnInit {
    applicant: Applicant;
    applicantJobFunnelMessageTemplate: JobFunnelMessageTemplate;
    jobPostingsApplicantCanBeCopiedTo: JobPosting[] = [];
    funnelStagesFromSelectedJobPosting: JobFunnel[] = [];
    selectedCopyAction = ApplicantCopyActions.Copy;
    backRoute = '';
    isLoading = false;
    previousApplicantId: number;
    nextApplicantId: number;
    applicantRoute = '/recruiting/applicants';
    selectedJobPosting: JobPosting | null = null;
    selectedFunnelStage: JobFunnel | null = null;
    hasRecruiterAccess = false;

    // Dialogs
    isChangeFunnelStageModalOpen = false;
    isMoveAndCopyApplicantDialogOpen = false;
    isDialogLoading = false;
    isDeleteApplicantDialogOpen = false;

    applicantIsHired = false;

    jobFunnelDisplay = JobFunnelNameOptions;

    badgeSuccess: BadgeOptions = {
        label: 'applicant-tracker.active',
        type: 'success',
    };

    badgeWarning: BadgeOptions = {
        label: 'applicant-tracker.archived',
        type: 'warning',
    };

    badgeHired: BadgeOptions = {
        label: 'applicant-tracker.hired',
        type: 'informative',
    };

    constructor(
        public applicantService: ApplicantService,
        public tabMenuService: ApplicantProfileMenuService,
        public applicantIndividualMessageService: ApplicantIndividualMessageService,
        private route: ActivatedRoute,
        private auth: AuthService,
        private notify: NotifyService,
        private analyticService: AnalyticService,
        private router: Router,
        private location: Location,
        private applicantProfileService: ApplicantProfileService,
        private routingState: RoutingStateService
    ) {
        this.isLoading = true;
    }

    async ngOnInit(): Promise<void> {
        // Clear cache for any previous data leftover in the service
        this.applicantProfileService.clearAllCaches();

        this.applicant = this.route.snapshot.data.models.get('applicant');

        this.applicantProfileService.applicant = this.applicant;

        this.applicantIsHired = this.applicant.isHired;

        this.hasRecruiterAccess = this.auth.can(Platform.recruiting.permission.recruiter);

        await this.setPrevAndNextApplicant();

        this.backRoute = this.getBackRoute();

        this.isLoading = false;
    }

    async onRate(): Promise<void> {
        const originalRating = this.applicant.getOriginalAttribute('rating');

        if (originalRating === this.applicant.rating) {
            this.applicant.rating = 0;
        }

        await this.saveApplicantWithoutLoadingState();

        this.notify.success('applicant-tracker.applicants.ratingSaved');
    }

    prevCandidate(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantNavigation);
        // Clear the cache before navigating to a new employee since this will force the service to pull new data.
        this.applicantProfileService.clearAllCaches();
        this.router.navigate([this.applicantRoute, this.previousApplicantId], {
            relativeTo: this.route,
        });
    }

    nextCandidate(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantNavigation);
        // Clear the cache before navigating to a new employee since this will force the service to pull new data.
        this.applicantProfileService.clearAllCaches();
        this.router.navigate([this.applicantRoute, this.nextApplicantId], {
            relativeTo: this.route,
        });
    }

    getFunnels(): JobFunnel[] {
        return this.applicant.jobPosting?.jobFunnels
            .sort((a: JobFunnel, b: JobFunnel) => {
                if (a.funnelIndex < b.funnelIndex) {
                    return -1;
                }
                return 1;
            })
            .map((funnel: JobFunnel) => {
                const translatedStep = JobFunnelNameOptions[funnel.funnelStep];

                if (translatedStep && typeof translatedStep === 'string') {
                    funnel.funnelStep = translatedStep;
                }

                return funnel;
            });
    }

    onFunnelChange(applicant: Applicant): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantProfileChangeApplicantFunnelStage);
        applicant
            .param('company', this.auth.company.id)
            .save()
            .catch((err) => {
                this.notify.error(err.message || 'applicant-tracker.applicants.couldNotSaveApplicant');
            });

        this.onChangeFunnelStage();
    }

    async onJobPostingSelectionChange(): Promise<void> {
        if (!this.selectedJobPosting) {
            return;
        }

        this.isDialogLoading = true;

        const { jobFunnels } = await JobPosting.param('company', this.auth.company.id)
            .with(['jobFunnels'])
            .find(this.selectedJobPosting.id);

        this.funnelStagesFromSelectedJobPosting = jobFunnels;
        this.isDialogLoading = false;
    }

    async onCopyApplicant(): Promise<void> {
        if (this.selectedCopyAction === ApplicantCopyActions.Copy) {
            this.isDialogLoading = true;

            try {
                const newApplicant = await this.applicantService.copyApplicant(
                    this.applicant,
                    this.selectedJobPosting,
                    this.selectedFunnelStage
                );

                this.notify.success({
                    key: 'applicant-tracker.applicants.applicantCopiedToTitle',
                    params: { title: this.selectedJobPosting.title },
                });

                this.analyticService.trackEvent(
                    AnalyticEvents.RecruitingApplicantProfileMeatballCopyApplicantClickConfirmToSave
                );

                // We need to update the service to use the new applicant id and to push to the subject.
                this.applicantProfileService.applicant = newApplicant;

                this.router.navigate([this.applicantRoute, newApplicant.id], {
                    relativeTo: this.route,
                });
            } catch (err) {
                this.notify.error(
                    err.error?.errors?.map((e) => e.detail).join('\n') ||
                        err.message ||
                        'applicant-tracker.applicants.couldNotCopyApplicantToJobPosting'
                );
            }
        }

        if (this.selectedCopyAction === ApplicantCopyActions.Move) {
            this.isDialogLoading = true;

            try {
                await this.applicantService.moveApplicant(
                    this.applicant,
                    this.selectedJobPosting,
                    this.selectedFunnelStage
                );

                this.notify.success({
                    key: 'applicant-tracker.applicants.applicantMovedToTitle',
                    params: { title: this.selectedJobPosting.title },
                });

                this.analyticService.trackEvent(
                    AnalyticEvents.RecruitingApplicantProfileMeatballMoveApplicantClickConfirmToSave
                );

                // This will cause the subject in the service to emit fresh data to underlying components that rely on it.
                this.refreshApplicant();
            } catch (err) {
                this.notify.error(
                    err.error?.errors?.map((e) => e.detail).join('\n') ||
                        err.message ||
                        'applicant-tracker.applicants.couldNotMoveApplicantToJobPosting'
                );
            }
        }

        await this.applicantProfileService.refreshApplicant();

        this.selectedJobPosting = null;
        this.selectedFunnelStage = null;

        this.isMoveAndCopyApplicantDialogOpen = false;
        this.isDialogLoading = false;
    }

    async onChangeFunnelStage(): Promise<void> {
        const jobFunnel: JobFunnel = await JobFunnel.with(['jobFunnelMessageTemplate']).find(
            this.applicant.jobFunnelId
        );

        if (!jobFunnel.jobFunnelMessageTemplate) {
            this.notify.success('applicant-tracker.applicants.applicantStatusChanged');
            return;
        }

        this.isChangeFunnelStageModalOpen = true;

        this.applicantJobFunnelMessageTemplate = jobFunnel.jobFunnelMessageTemplate;
    }

    onCloseChangeFunnelStage(): void {
        this.isChangeFunnelStageModalOpen = false;

        this.notify.success('applicant-tracker.applicants.applicantStatusChanged');
    }

    async onConfirmChangeFunnelStage(): Promise<void> {
        try {
            await this.applicantIndividualMessageService.sendApplicantIndividualMessage(
                this.applicant.id,
                this.applicantJobFunnelMessageTemplate
            );

            this.notify.success({
                key: 'applicant-tracker.applicants.subjectEmailSentToApplicant',
                params: { subject: this.applicantJobFunnelMessageTemplate.subject },
            });

            this.notify.success('applicant-tracker.applicants.applicantStatusChanged');
        } catch (err) {
            this.notify.error({
                key: 'applicant-tracker.applicants.emailCouldNotBeSentToApplicant',
                params: { subject: this.applicantJobFunnelMessageTemplate.subject },
            });
            this.notify.error('applicant-tracker.applicants.applicantStatusChangeFailed');
        }

        this.isChangeFunnelStageModalOpen = false;
    }

    onArchive(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingArchiveApplicant);
        this.applicant.archivedAt = format(new Date(), 'YYYY-MM-DD');
        this.applicant.isArchived = true;
        this.applicant.archivedBy = this.auth.user.id;
        this.applicant.save().catch((err) => {
            this.notify.error(err.message || 'applicant-tracker.applicants.couldNotBeArchived');
        });
    }

    onUnarchive(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingUnarchiveApplicant);
        this.applicant.archivedAt = null;
        this.applicant.isArchived = null;
        this.applicant.archivedBy = null;
        this.applicant.save().catch((err) => {
            this.notify.error(err.message || 'applicant-tracker.applicants.applicantCouldNotBeUnarchived');
        });
    }

    onHire({ pathParams, queryParams }: HireClickEvent): void {
        if (!this.auth.canAll(HireEmployee.permissions)) {
            this.notify.error('applicant-tracker.applicants.youDoNotHavePermissionsToHireEmployees');
            return;
        }

        this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantProfileMeatballClickHire);
        this.router.navigate([...pathParams], {
            queryParams,
        });
    }

    onSendOffer({ pathParams, queryParams }: SendOfferClickEvent): void {
        if (!this.auth.canAll(HireEmployee.permissions)) {
            this.notify.error('applicant-tracker.applicants.youDoNotHavePermissionsToSendOfferLetters');
            return;
        }

        this.router.navigate([...pathParams], {
            queryParams,
        });
    }

    onDelete(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingDeleteApplicant);

        this.applicant
            .delete()
            .then(() => {
                this.notify.success('applicant-tracker.applicants.applicantHasBeenDeleted');
                this.location.back();
            })
            .catch((err) => {
                this.notify.error(err.message || 'applicant-tracker.applicants.applicantCouldNotBeDeleted');
            });
    }

    closeDeleteApplicantDialog(): void {
        this.isDeleteApplicantDialogOpen = false;
    }

    openDeleteApplicantDialog(): void {
        this.isDeleteApplicantDialogOpen = true;
    }

    async showMoveAndCopyApplicantDialog(): Promise<void> {
        if (!this.jobPostingsApplicantCanBeCopiedTo.length) {
            this.isLoading = true;
            const [jobPostings] = await JobPosting.param('company', this.auth.company.id)
                .whereIn('status', [JobPostingStatus.Active, JobPostingStatus.Archived, JobPostingStatus.Paused])
                .orderBy('createdAt', 'desc')
                .all();

            this.jobPostingsApplicantCanBeCopiedTo = jobPostings.filter(
                (jobPosting) => jobPosting.id !== this.applicant.jobPostingId
            );

            this.isLoading = false;
        }

        this.isMoveAndCopyApplicantDialogOpen = true;
    }

    closeMoveAndCopyApplicantDialog(): void {
        this.isMoveAndCopyApplicantDialogOpen = false;
    }

    private async refreshApplicant(): Promise<void> {
        this.applicant = await Applicant.param('company', this.auth.company.id)
            .with(['jobPosting.hiringTeam', 'jobPosting.jobFunnels', 'jobFunnel'])
            .find(this.applicant.id);
    }

    private getBackRoute(): string {
        const defaultRecruitingRoute = '/recruiting';
        const excludeRouteFromHistoryWhichStartsWith = '/recruiting/applicants/';

        //This is being excluded to avoid navigation loop in recruiting - otherwise it keeps track of interview
        const excludeApplicantInterviewRouteFromHistoryWhichStartsWith = 'interviews/applicantInterviews';

        const history = this.routingState.history.filter(
            (route: string) =>
                !route.includes(excludeRouteFromHistoryWhichStartsWith) &&
                !route.includes(excludeApplicantInterviewRouteFromHistoryWhichStartsWith)
        );

        return history[history.length - 1] ?? defaultRecruitingRoute;
    }

    private async saveApplicant(isLoading = true): Promise<void> {
        this.isLoading = isLoading;

        await this.applicant
            .param('company', this.auth.company.id)
            .save()
            .catch((err) => {
                this.notify.error(err.message || 'Could not save applicant');
            });

        this.isLoading = false;
    }

    private async saveApplicantWithoutLoadingState(): Promise<void> {
        await this.saveApplicant(false);
    }

    private async setPrevAndNextApplicant(): Promise<void> {
        const paginator = this.applicantService.indexPaginator;
        const context =
            this.applicantService.pageContext === 'index' ||
            this.applicantService.pageContext === JobPostingApplicantIndex
                ? 'index'
                : 'funnel';
        // Default orderby if the user comes to the page directly
        let request = Applicant.queryParams('showContext', context);
        // If we come from a specific job posting page we need to filter the results to make sure the prev and next work properly
        if (
            this.applicantService.pageContext === JobPostingApplicantIndex ||
            this.applicantService.pageContext === PipelineShow
        ) {
            request = request.where('atsJobId', this.applicant.job.id);
        }

        if (paginator) {
            if (Object.keys(paginator.filters).length) {
                for (const [key, value] of Object.entries(paginator.filters)) {
                    request = request.whereIn(key, value);
                }
            }

            if (Object.keys(paginator.sort).length) {
                request = request.orderBy(paginator.sort.column, paginator.sort.order);
            }
        }

        const applicant: Applicant = await request.find(this.applicant.id);
        this.previousApplicantId = applicant.previousApplicantId;
        this.nextApplicantId = applicant.nextApplicantId;
    }
}
